/**
 * `multiChoice` — tick every one that applies.
 *
 * The comparison is still nothing but id equality, done against a set instead of a single value.
 * What makes this the longest of the item scorers is not the comparison, it is that **"select all
 * that apply" is the one item type where established systems genuinely disagree about marking**,
 * so the rule is a scheme field with three values rather than a decision baked in here.
 *
 * `all` is the default and needs no defending: exact set match, which is QTI's `match_correct` and
 * Blackboard's default. The other two are below, each with the reason it exists and the objection
 * to it, because both have one.
 *
 * One thing common to all three: `parts` covers the union of ticked and correct, never the full
 * option list, because this scorer does not have the option list — only a count, and only when
 * `perOption` asked for it. So a distractor the learner correctly left alone gets no part, which
 * is also the right answer for a learner: an untouched wrong option is not a row to look at.
 */

import { isMultiChoiceResponse } from '@cet/asset-contract';
import type { ItemResponse, ItemVerdict, Locale, MultiChoiceSchemeDoc, VerdictPart } from '@cet/asset-contract';

import { ScoringError } from '../errors.js';
import { graded, unanswered } from '../verdicts.js';

export function scoreMultiChoice(
  itemId: string,
  scheme: MultiChoiceSchemeDoc,
  response: ItemResponse | null | undefined,
  locale: Locale,
): ItemVerdict {
  if (response === null || response === undefined) {
    return unanswered(itemId, scheme.points);
  }

  if (!isMultiChoiceResponse(response)) {
    throw new ScoringError(
      'invalid-response',
      `Item "${itemId}" is a multiChoice item, so its response must be { choiceIds: string[] } — ` +
        `note the plural; { choiceId } is a single-choice response. Received keys: ` +
        `${Object.keys(response).join(', ') || '(none)'}.`,
      `responses.${itemId}`,
    );
  }

  // Nothing ticked is a blank, not a wrong answer (D32).
  if (response.choiceIds.length === 0) {
    return unanswered(itemId, scheme.points);
  }

  /**
   * Deduplicated before anything is counted. A UI that reports the same id twice would otherwise
   * score it twice — as two hits, or as two misses — and the learner ticked one box. Cheaper to be
   * indifferent to it here than to make every asset guarantee uniqueness.
   */
  const ticked = new Set(response.choiceIds);
  const correct = new Set(scheme.correct);

  let hits = 0;
  let misses = 0;
  for (const id of ticked) {
    if (correct.has(id)) hits += 1;
    else misses += 1;
  }

  /**
   * One part per id that is either ticked or correct, `correct: true` only for a right tick. So a
   * wrong tick and a right answer left blank both come back as `correct: false` — which is the
   * same statement in both cases, "this row is wrong", and that is all a VerdictPart claims.
   *
   * Sorted, so nothing about the output can depend on the order the learner happened to tick or
   * the order the scheme happened to list: the browser and the server must agree byte for byte.
   */
  const parts: VerdictPart[] = [...new Set([...ticked, ...correct])]
    .sort()
    .map((id) => ({ id, correct: ticked.has(id) && correct.has(id) }));

  // Exactly the right set: every correct id ticked and nothing else. `misses === 0` plus
  // `hits === correct.size` says both halves, and neither alone would.
  const whollyCorrect = misses === 0 && hits === correct.size;

  return graded(
    itemId,
    {
      // A wholly correct answer is paid in full by every rule, spelled out here rather than left
      // to each formula to reach by flooring — `partial` and `perOption` both land on it exactly,
      // and relying on that is how a rounding change would quietly stop paying full marks.
      awarded: whollyCorrect ? scheme.points : awardFor(itemId, scheme, hits, misses),
      max: scheme.points,
    },
    scheme.feedback,
    whollyCorrect,
    locale,
    parts,
  );
}

/** Integer arithmetic throughout: scores never accumulate as floats. */
function awardFor(
  itemId: string,
  scheme: MultiChoiceSchemeDoc,
  hits: number,
  misses: number,
): number {
  const correctCount = new Set(scheme.correct).size;

  switch (scheme.credit ?? 'all') {
    case 'all':
      // Handled by the caller's `whollyCorrect` branch; anything reaching here is not the set.
      return 0;

    /**
     * The LMS consensus rule, in its simplest form: a right tick earns `1 ÷ correctCount`, a wrong
     * tick costs the same, and the result floors at zero so an item can never take points away.
     *
     * Ticking every box scores `correctCount − distractors`, so it is never full marks and is zero
     * whenever an item has at least as many distractors as answers. It is *not* zero when
     * distractors are outnumbered — three correct among two distractors pays 1 of 3 — which is a
     * reason to write enough distractors rather than a defect in the rule. The degenerate end,
     * every option correct, is rejected by `contract/choices-resolve` at authoring time.
     */
    case 'partial':
      return Math.floor((scheme.points * Math.max(0, hits - misses)) / correctCount);

    /**
     * Every option is an independent decision, and **leaving a distractor alone counts as a right
     * one**. The rule for multiple true-false items, where an unticked box means "no" rather than
     * "not answered".
     *
     * `optionCount` is required by the schema for this rule, so its absence here means a
     * hand-edited or newer-contract scheme reached an older scorer. That throws rather than
     * guessing a denominator: silently substituting `correctCount` would grade every learner on
     * this item under a rule nobody chose, and the arithmetic would look entirely plausible.
     */
    case 'perOption': {
      const total = scheme.optionCount;
      if (total === undefined || total < correctCount) {
        throw new ScoringError(
          'invalid-scheme',
          `Item "${itemId}" uses "credit": "perOption", which scores every option as its own ` +
            `right-or-wrong decision and therefore needs "optionCount" — the number of choices ` +
            `the item shows in total. ` +
            `${total === undefined ? 'It is missing.' : `It says ${total}, which is fewer than the ${correctCount} correct answer(s).`}`,
          `items.${itemId}.optionCount`,
        );
      }

      // Right decisions = correct boxes ticked + distractors correctly left alone. The second term
      // is what makes this rule generous, and it is the whole reason it is not the default.
      const distractorsLeftAlone = total - correctCount - misses;
      const rightDecisions = hits + Math.max(0, distractorsLeftAlone);
      return Math.floor((scheme.points * rightDecisions) / total);
    }
  }
}
